import { Badge, IconWaiting, IconWarning } from '@ccp/ui';
import { useTranslation } from 'react-i18next';
import type { ConnectionStatus } from './connection.machine.js';

export interface ConnectionBannerProps {
  readonly status: ConnectionStatus;
}

/**
 * What the agent is told about the connection.
 *
 * It says something only when it has to. `live` and `idle` render nothing: a banner that is always
 * there is furniture, and the one thing this has to do is be noticed when the screen may be wrong.
 *
 * `aria-live="polite"` rather than `assertive`: it is said once, after whatever the agent is in the
 * middle of, because interrupting a screen-reader user mid-call to tell them the dashboard is
 * catching up is worse than telling them a second later.
 */
export function ConnectionBanner({ status }: ConnectionBannerProps) {
  const { t } = useTranslation();

  if (status === 'live' || status === 'idle') {
    // Still a live region, and still in the tree: a region that appears only when it has something
    // to say is a region some screen readers never announce.
    return <div aria-live="polite" className="sr-only" />;
  }

  const degraded = status === 'degraded';
  return (
    <div
      aria-live="polite"
      className="flex items-center gap-(--space-sm) border-b border-(--color-border) bg-(--color-warning-subtle) px-(--space-lg) py-(--space-xs) md:px-(--space-2xl)"
    >
      {degraded ? (
        <IconWarning aria-hidden="true" className="size-(--size-icon-sm) text-(--color-warning)" />
      ) : (
        <IconWaiting aria-hidden="true" className="size-(--size-icon-sm) text-(--color-warning)" />
      )}
      <p className="text-(length:--font-size-caption) text-(--color-text-primary)">
        {t(`connection.${status}`)}
      </p>
      <Badge tone="warning">{t(`connection.label.${status}`)}</Badge>
    </div>
  );
}
